import { IonButton, IonIcon, IonInput } from "@ionic/react";
import { addOutline, removeOutline } from "ionicons/icons";
import { useCallback } from "react";

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  placeholder?: string;
}

export default function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  placeholder,
}: NumberInputProps) {
  const handleDecrement = useCallback(() => {
    const newValue = value - step;
    if (min !== undefined && newValue < min) return;
    onChange(newValue);
  }, [value, step, min, onChange]);

  const handleIncrement = useCallback(() => {
    const newValue = value + step;
    if (max !== undefined && newValue > max) return;
    onChange(newValue);
  }, [value, step, max, onChange]);

  const handleInputChange = useCallback(
    (e: CustomEvent) => {
      const inputValue = parseFloat(e.detail.value ?? "0");
      if (isNaN(inputValue)) return;

      let newValue = inputValue;
      if (min !== undefined && newValue < min) newValue = min;
      if (max !== undefined && newValue > max) newValue = max;

      onChange(newValue);
    },
    [min, max, onChange]
  );

  const isDecrementDisabled = disabled || (min !== undefined && value <= min);
  const isIncrementDisabled = disabled || (max !== undefined && value >= max);

  return (
    <div className="ion-display-flex mb-1">
      <IonButton
        fill="solid"
        size="default"
        disabled={isDecrementDisabled}
        onClick={handleDecrement}
        className="ion-margin-end"
      >
        <IonIcon slot="icon-only" icon={removeOutline} />
      </IonButton>
      <IonInput
        className="flex-1 ion-text-center"
        type="number"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step?.toString()}
        onIonInput={handleInputChange}
      />
      <IonButton
        fill="solid"
        size="default"
        disabled={isIncrementDisabled}
        onClick={handleIncrement}
        className="ion-margin-start"
      >
        <IonIcon slot="icon-only" icon={addOutline} />
      </IonButton>
    </div>
  );
}
